/* eslint-disable react-hooks/exhaustive-deps */
import { AptunoButton, CheckBox, HeaderSimple, Icon, Input, SIZES, Stepper, VARIANTS } from '@aptuno/aptuno-ui';
import { DateInput } from '@components/commons/DatePicker/DatePicker';
import { ApplicationStateSteps, estateApplicationStageContext, EstateApplicationSteps } from '@contexts/Estate';
import { DniCode, DniType, IssueDate } from '@contexts/Estate/EstateApplication/domain';
import {
  ApplicationPersonalInfoCompleted,
} from '@contexts/Estate/EstateApplication/domain/events/application-personal-info-completed.event';
import {
  IdentityValidationError,
  identityValidationErrors,
  useIdentityValidationActions,
} from '@contexts/IdentityValidation';
import { getDNIType } from '@contexts/IdentityValidation/domain';
import {
  AptunoError,
  AuthenticatedUser,
  DocumentTypes,
  DocumentTypesOptions,
  errorMessage,
  errorMessages,
  GeneralAptunoErrorType,
  Option,
  Response,
  UserContext,
} from '@contexts/Shared';
import { Capitalize, useTrackingSource } from '@contexts/Shared/utils';
import { useAnalyticsTracker } from '@services/analytics';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DocumentFields } from '..';
import { cotenantInformationErrors, tenantInformationErrors } from '../../errors';
import { useTenantInformationActions } from '../../store';
import { ViewError } from '../../Types';
import { EaseInModalError } from '../EaseInModal/EaseInModal';
import {
  ActionsBarRoot,
  ContentBox,
  FormWrapper,
  IconClose,
  InputGroup,
  ProcessContainer,
  SelectContainer,
} from '../EstateApplication.styled';
import { TermsAndConditionsSection } from './ApplicantBasicInfo.styled';
import dynamic from 'next/dynamic';

const Select = dynamic(
  () => import('react-functional-select').then((mod) => mod.Select),
  { ssr: false }
);

const registerOptions = {
  firstName: { required: true },
  lastName: { required: true },
  email: {
    required: true,
    pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
  },
  dniType: { required: true },
  dniType2: { required: true },
  dniNumber: { required: true },
  dniNumber2: { required: true },
  issueDate: { required: true },
  termsAndConditions: { required: true },
};

const mapFormToBack = (formData: any): AuthenticatedUser => {
  return {
    firstName: Capitalize(formData.firstName),
    lastName: Capitalize(formData.lastName),
    email: formData.email.toLowerCase(),
    identityDocumentType: formData.dniType.name,
    identityDocumentCode: formData.dniNumber,
    identityDocumentIssueDate: formData.issueDate as Date,
  };
};

const hidePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) {
    return '';
  }
  if (phoneNumber.length === 13) {
    return phoneNumber.replace(/(\d{2})(\d{8})(\d{2})/, '$1*******$3');
  }
  if (phoneNumber.length < 13) {
    return phoneNumber.replace(/(\d{2})(\d{7})(\d{2})/, '$1******$3');
  }
  return phoneNumber.replace(/(\d{2})(\d{9})(\d{2})/, '$1********$3');
};

const getUpdateUserErrorMsg = (
  error: AptunoError,
  isTenant: boolean
): ViewError => {
  const baseError = isTenant
    ? tenantInformationErrors[error.code] || tenantInformationErrors['DEFAULT']
    : cotenantInformationErrors[error.code] ||
      cotenantInformationErrors['DEFAULT'];
  const registeredPhone = error.params?.identityDocumentOwnerPhone;
  const errorMsg =
    baseError.textSecondary?.replace('{0}', hidePhoneNumber(registeredPhone)) ||
    '';

  return {
    ...baseError,
    textSecondary: errorMsg,
  };
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  dniType: { name: string; value: DniType } | undefined;
  dniType2: { name: string; value: DniType } | undefined;
  dniNumber: string;
  dniNumber2: string;
  issueDate: Date | undefined;
  termsAndConditions: boolean | undefined;
};

const ApplicantBasicInfo = ({ isIsolatedPage = false }) => {
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, dirtyFields, touchedFields },
  } = useForm<FormValues>({
    mode: 'onBlur',
  });

  const currentStep = 0;
  const estateApplication = useContext(estateApplicationStageContext);
  const user = useContext(UserContext);
  const router = useRouter();
  const originSource = useTrackingSource();
  const eventTracker = useAnalyticsTracker();

  const {
    updatedTenantInformation,
    error: errorUpdateTenantInformation,
    loading: loadingUpdateTenantInformation,
    updateTenantInformation,
  } = useTenantInformationActions().tenantInformationCommandActions.useUpdateTenantInformation();

  const { result: identityValidation, get: getIdentityValidation } =
    useIdentityValidationActions().identityValidationQueryActions.useGetIdentityValidation();

  const {
    execute: approveIdentityValidation,
    result: approvedIdentityValidation,
  } =
    useIdentityValidationActions().identityValidationCommandActions.useApproveIdentityValidation();

  const getIssueDate = () =>
    Response.isSucceed(identityValidation)
      ? identityValidation.result.dni?.issueDate
      : undefined;

  const [documentType, setDocumentType] = useState<DniType | undefined>(
    undefined
  );

  const handleOnBackClick = () => {
    estateApplication &&
      estateApplication.setCurrentState(ApplicationStateSteps.documentsCheck);
  };

  const setNextStep = () => {
    estateApplication &&
      estateApplication.setCurrentState(
        ApplicationStateSteps.applicantCharacterization
      );
  };

  const onSubmit = (data) => {
    const mappedUserData = mapFormToBack(data);

    if (
      Response.isSucceed(identityValidation) &&
      identityValidationDoesNotHaveDni()
    ) {
      approveIdentityValidation({
        identityValidationId: identityValidation.result.id,
        identityDocumentType: getDNIType(mappedUserData.identityDocumentType),
        identityDocumentCode: new DniCode(mappedUserData.identityDocumentCode),
        identityDocumentIssueDate: new IssueDate(
          mappedUserData.identityDocumentIssueDate!.getTime()
        ),
      });
    }
  };

  const getDniTypeOptionValue = useCallback(
    (option: Option<DocumentTypes>): string => option.value,
    []
  );

  const getDniTypeOptionLabel = useCallback(
    (option: Option<DocumentTypes>): string => `${option.value}`,
    []
  );

  const isUserLoaded = Boolean(user && user.data);
  const getUserDocumentType =
    user &&
    DocumentTypesOptions.find(
      (type) => type.name === user.data?.identityDocumentType
    );

  useEffect(() => {
    const isDniAlreadyExistError =
      Response.isError(approvedIdentityValidation) &&
      IdentityValidationError.isDniAlreadyUsedError(
        approvedIdentityValidation.error as AptunoError
      );

    const isApprovedIdentityValidation = Response.isSucceed(
      approvedIdentityValidation
    );

    if (isApprovedIdentityValidation) {
      const mappedUserData = mapFormToBack(control._formValues);

      updateTenantInformation({ editUserInfoData: mappedUserData });
    } else if (isDniAlreadyExistError) {
      setShowErrorModal(
        () =>
          identityValidationErrors[
            (approvedIdentityValidation.error as AptunoError).code
          ]
      );
    }
  }, [approvedIdentityValidation.type]);

  useEffect(() => {
    if (user && user.data) {
      loadUserData(user.data);
      getIdentityValidation();
    }
  }, [user]);

  const loadUserData = (user: AuthenticatedUser) => {
    setValue('firstName', user.firstName);
    setValue('lastName', user.lastName);
    setValue('email', user.email);
    setValue(
      'dniType',
      DocumentTypesOptions.find(
        (type) => type.name === user.identityDocumentType
      )
    );
    setValue('dniNumber', user.identityDocumentCode);
    setValue('issueDate', getIssueDate());
  };

  useEffect(() => {
    if (
      updatedTenantInformation &&
      !loadingUpdateTenantInformation &&
      !errorUpdateTenantInformation
    ) {
      segmentTrack();
      setNextStep();
    }
  }, [
    updatedTenantInformation,
    loadingUpdateTenantInformation,
    errorUpdateTenantInformation,
  ]);

  const handleOnLeave = () => {
    estateApplication &&
      estateApplication.setCurrentState(ApplicationStateSteps.leaveApplication);
  };

  const segmentTrack = () =>
    user?.data?.id &&
    eventTracker.trackEvent(
      new ApplicationPersonalInfoCompleted(originSource, user.data.id)
    );

  const validateNaturalPerson = () => documentType !== DniType.NIT;

  const [showErrorModal, setShowErrorModal] = useState<ViewError | undefined>();

  const isTenantFlow = !router.query.applicationId;

  useEffect(() => {
    if (errorUpdateTenantInformation) {
      setShowErrorModal(
        getUpdateUserErrorMsg(errorUpdateTenantInformation, isTenantFlow)
      );
    }
  }, [errorUpdateTenantInformation]);

  const filterDocumentTypeOptions = (
    isTenantFlow: boolean
  ): Option<DniType>[] => {
    if (!isTenantFlow) {
      return DocumentTypesOptions;
    }
    return DocumentTypesOptions.filter(
      (type) => type.name !== DocumentTypes.NIT
    );
  };

  const identityValidationWasNotFound = () =>
    Response.isError(identityValidation) &&
    GeneralAptunoErrorType.isResourceNotFound(
      identityValidation.error as AptunoError
    );

  const identityValidationDoesNotHaveDni = () =>
    Response.isSucceed(identityValidation) &&
    !identityValidation.result.haveDni();

  const onIdentityDocumentCodeMatchFailed = () => {
    setValue('dniNumber', '');
    setValue('dniNumber2', '');
    return errorMessage(`Los números de documento ${errorMessages.match}`);
  };

  return (
    <div>
      {showErrorModal && (
        <EaseInModalError
          size={600}
          isActive={showErrorModal}
          onClose={() => setShowErrorModal(undefined)}
          title={showErrorModal.title}
          onAcceptButtonClick={() => setShowErrorModal(undefined)}
          btnText={`${showErrorModal.btnText}`}
          mainText={showErrorModal.text}
          secondaryText={showErrorModal.textSecondary}
        />
      )}

      <HeaderSimple
        withBorderRadius={false}
        onButtonBack={handleOnBackClick}
        maxWidth="100%"
        isFixed={false}
      >
        <h3>Estudio de Riesgo</h3>
        <IconClose
          icon={'closeAlt'}
          color={'#9B9B9B'}
          marginRight={'16px !important'}
          onClick={handleOnLeave}
        />
      </HeaderSimple>
      <ProcessContainer>
        <Stepper steps={EstateApplicationSteps} activeStep={currentStep} />
      </ProcessContainer>
      <ContentBox>
        <FormWrapper id={'basicInfo'} onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <label>Nombre</label>
            <Input
              type="text"
              placeholder="Ejemplo: Juanita"
              {...register('firstName', registerOptions.firstName)}
              invalid={!!errors.firstName}
              disabled={
                control._formValues['firstName']?.length > 0 &&
                !identityValidationDoesNotHaveDni()
              }
            />
            {errors.firstName?.type === 'required' &&
              errorMessage(`El nombre ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>Apellido</label>
            <Input
              type="text"
              placeholder="Ejemplo: Benavides"
              {...register('lastName', registerOptions.lastName)}
              invalid={!!errors.lastName}
              disabled={
                control._formValues['lastName']?.length > 0 &&
                !identityValidationDoesNotHaveDni()
              }
            />
            {errors.lastName?.type === 'required' &&
              errorMessage(`El nombre ${errorMessages.required}`)}
          </InputGroup>
          <InputGroup>
            <label>Email</label>
            <Input
              type="email"
              placeholder="Ejemplo: juanita@aptuno.com"
              {...register('email', registerOptions.email)}
              invalid={!!errors.email}
              disabled={
                control._formValues['email']?.length > 0 &&
                !identityValidationDoesNotHaveDni()
              }
            />
            {errors.email?.type === 'required' &&
              errorMessage(`Email ${errorMessages.required}`)}
            {errors.email?.type === 'pattern' &&
              errorMessage(`Email ${errorMessages.pattern}`)}
          </InputGroup>
          <InputGroup>
            <label>Documento de identidad</label>
            <DocumentFields>
              <Controller
                control={control}
                {...register('dniType', registerOptions.dniType)}
                render={({ field }) => {
                  setDocumentType(field.value?.value);
                  return (
                    <SelectContainer width={'96px'} invalid={!!errors.dniType}>
                      {isUserLoaded && (
                        <Select
                          isDisabled={
                            Boolean(field.value) &&
                            !identityValidationDoesNotHaveDni()
                          }
                          isSearchable
                          placeholder="Tipo"
                          initialValue={getUserDocumentType}
                          options={filterDocumentTypeOptions(isTenantFlow)}
                          onOptionChange={field.onChange}
                          getOptionValue={getDniTypeOptionValue}
                          getOptionLabel={getDniTypeOptionLabel}
                        />
                      )}
                    </SelectContainer>
                  );
                }}
              />
              <Input
                width={'100%'}
                type="text"
                placeholder="Número de documento"
                autoComplete="off"
                onPaste={(e: any) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e: any) => {
                  e.preventDefault();
                  return false;
                }}
                invalid={!!errors.dniNumber}
                {...register('dniNumber', registerOptions.dniNumber)}
                disabled={
                  control._formValues['dniNumber']?.length > 0 &&
                  !identityValidationDoesNotHaveDni()
                }
              />
            </DocumentFields>
            {errors.dniType?.type === 'required' &&
              errorMessage(`El tipo de documento ${errorMessages.required}`)}
            {errors.dniNumber?.type === 'required' &&
              errorMessage(`El número de documento ${errorMessages.required}`)}
          </InputGroup>
          {(identityValidationWasNotFound() ||
            identityValidationDoesNotHaveDni()) && (
            <InputGroup>
              <label>Confirma tu documento de identidad</label>
              <DocumentFields>
                <Controller
                  control={control}
                  {...register('dniType2', {
                    ...registerOptions.dniType2,
                    validate: (value) =>
                      value?.value === getValues().dniType?.value,
                  })}
                  render={({ field }) => {
                    setDocumentType(field.value?.value);
                    return (
                      <SelectContainer
                        width={'96px'}
                        invalid={!!errors.dniType}
                      >
                        {isUserLoaded && (
                          <Select
                            isSearchable
                            placeholder="Tipo"
                            options={filterDocumentTypeOptions(isTenantFlow)}
                            onOptionChange={field.onChange}
                            getOptionValue={getDniTypeOptionValue}
                            getOptionLabel={getDniTypeOptionLabel}
                          />
                        )}
                      </SelectContainer>
                    );
                  }}
                />
                <Input
                  width={'100%'}
                  type="text"
                  placeholder="Número de documento"
                  onPaste={(e: any) => {
                    e.preventDefault();
                    return false;
                  }}
                  onCopy={(e: any) => {
                    e.preventDefault();
                    return false;
                  }}
                  autoComplete="off"
                  invalid={!!errors.dniNumber}
                  {...register('dniNumber2', {
                    ...registerOptions.dniNumber,
                    validate: (value) => value === getValues().dniNumber,
                  })}
                />
              </DocumentFields>
              {errors.dniType2?.type === 'required' &&
                errorMessage(
                  `${errorMessages.comfirmationRequied} tu tipo de documento`
                )}
              {errors.dniNumber2?.type === 'required' &&
                errorMessage(
                  `${errorMessages.comfirmationRequied} tu número de documento`
                )}
              {errors.dniType2?.type === 'validate' &&
                errorMessage(`Los tipos de documento ${errorMessages.match}`)}
              {errors.dniNumber2?.type === 'validate' &&
                onIdentityDocumentCodeMatchFailed()}
            </InputGroup>
          )}
          {validateNaturalPerson() && (
            <InputGroup>
              <label>Fecha de Expedición</label>
              <Controller
                control={control}
                {...register('issueDate', registerOptions.issueDate)}
                render={({ field }) => {
                  return (
                    <DateInput
                      disabled={
                        !!field.value &&
                        Response.isSucceed(identityValidation) &&
                        !!identityValidation.result.dni?.issueDate
                      }
                      {...field}
                      invalid={!!errors.issueDate}
                      minDate={new Date(1922, 1, 1)}
                    />
                  );
                }}
              />
              {errors.issueDate?.type === 'required' &&
                errorMessage(
                  `La fecha de expedición ${errorMessages.requiredAlt}`
                )}
            </InputGroup>
          )}
          <TermsAndConditionsSection>
            <CheckBox
              key={'termsAndConditions'}
              id={'termsAndConditions'}
              {...register(
                'termsAndConditions',
                registerOptions.termsAndConditions
              )}
            />
            <p>
              Acepto los{' '}
              <Link href={'https://www.aptuno.com/terminos-y-condiciones'}>
                términos de uso
              </Link>{' '}
              y la política de{' '}
              <Link href={'https://www.aptuno.com/politica-de-privacidad'}>
                privacidad de datos
              </Link>{' '}
              de Aptuno. Además, autorizo a Aptuno y sus terceros habilitados a
              recolectar y utilizar mis datos personales según la ley y su
              política de privacidad de datos para los fines dispuestos.
              Igualmente a consultar mi información financiera y crediticia, así
              como realizar reportes positivos o negativos a centrales de riesgo
              y otras fuentes autorizadas.
            </p>
          </TermsAndConditionsSection>
          {errors.termsAndConditions?.type === 'required' &&
            errorMessage(`Debe aceptar los términos y condiciones`)}
        </FormWrapper>
        <ActionsBarRoot>
          <AptunoButton
            appearance={VARIANTS.PRIMARY}
            size={SIZES.BIGGEST}
            type={'submit'}
            form="basicInfo"
          >
            {loadingUpdateTenantInformation
              ? 'Guardando Información...'
              : 'Continuar'}
            <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
          </AptunoButton>
        </ActionsBarRoot>
      </ContentBox>
    </div>
  );
};

export { ApplicantBasicInfo };
